import React, { useContext, useEffect, useRef, useState } from 'react';
import { Table } from 'reactstrap';
import { AppContext } from '../application/provider';
import Loader from '../components/Loader/Loader';
import ValidacionServicioService from '../axios_services/validacionServicio.service';
import { ButtonInsert, ButtonSearch, ButtonUpdate } from '../components/Buttons/Buttons';
import ModalValidacionServicioForm from '../components/Modal/ModalValidacionServicioForm';
import ModalValidacionServicioPlanilla from '../components/Modal/ModalValidacionServicioPlanilla';
import { FileText } from 'lucide-react';
import './Induccion.css';

function ValidacionServicio() {
  const [state] = useContext(AppContext);
  const perfilId = Number(state.user?.perfil_id);
  const esTH = [5, 8, 22, 26].includes(perfilId);

  const [registros, setRegistros] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filtroPeriodo, setFiltroPeriodo] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [catalogo, setCatalogo] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [modalForm, setModalForm] = useState({ isOpen: false, row: null });
  const [modalPlanilla, setModalPlanilla] = useState({ isOpen: false, row: null });

  const initialFetchDone = useRef(false);

  const loadCatalogoYUsuarios = async () => {
    const [cat, usr] = await Promise.all([
      ValidacionServicioService.catalogo(),
      ValidacionServicioService.usuariosOpciones(),
    ]);
    if (cat.success) setCatalogo(cat.data);
    if (usr.success) setUsuarios(usr.data || []);
  };

  const loadData = async () => {
    setIsLoading(true);
    try {
      const params = {};
      if (filtroPeriodo) params.periodo = filtroPeriodo;
      const resp = await ValidacionServicioService.listar(params);
      setRegistros(resp.data || []);
    } catch (error) {
      setRegistros([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      (async () => {
        try {
          await loadCatalogoYUsuarios();
        } catch (e) {
          /* ignore */
        }
        await loadData();
      })();
    }
  }, []);

  const formatFecha = (f) => {
    if (!f) return '-';
    return String(f).split('T')[0];
  };

  const formatFechaHora = (f) => {
    if (!f) return '-';
    const s = String(f);
    return s.includes('T') ? `${s.split('T')[0]} ${s.split('T')[1]?.slice(0, 5) || ''}` : s;
  };

  const formatMonto = (v) => {
    if (v == null || v === '') return '-';
    const n = Number(v);
    if (Number.isNaN(n)) return '-';
    return n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const renderPlanillaCell = (r) => {
    const tiene = Number(r.tiene_planilla) === 1 && r.url_planilla_publica;
    if (tiene) {
      return (
        <button
          type="button"
          title="Ver planilla"
          className="acta-icon-doc acta-icon-doc--ok"
          onClick={() => window.open(r.url_planilla_publica, '_blank')}
        >
          <FileText size={18} strokeWidth={2} aria-hidden />
        </button>
      );
    }
    return (
      <button
        type="button"
        title="Subir planilla (PDF)"
        className="acta-icon-doc acta-icon-doc--falta"
        onClick={() => setModalPlanilla({ isOpen: true, row: r })}
      >
        <FileText size={18} strokeWidth={2} aria-hidden />
      </button>
    );
  };

  const th = (min) => ({ minWidth: min, whiteSpace: 'nowrap' });

  if (!esTH) return <div className="caja-flujo-container">No tiene permisos para acceder.</div>;

  if (isLoading && registros.length === 0) return <Loader />;

  return (
    <div className="caja-flujo-container induccion-modulo">
      <h1>VALIDACIÓN DE SERVICIO</h1>

      <div className="filtros induccion-filtros">
        <div className="campo-fecha">
          <label>Periodo:</label>
          <input type="month" value={filtroPeriodo} onChange={(e) => setFiltroPeriodo(e.target.value)} />
        </div>
      </div>

      <div className="filtros-actions">
        <ButtonSearch onClick={loadData} isLoading={isLoading} />
      </div>

      <div className="nuevo-movimiento-section">
        <ButtonInsert onClick={() => setModalForm({ isOpen: true, row: null })} />
      </div>

      <div className="table-container" style={{ overflowX: 'auto' }}>
        <Table hover size="sm" className="induccion-table" style={{ minWidth: '1480px' }}>
          <thead>
            <tr>
              <th style={th('200px')}>Colaborador</th>
              <th style={{ minWidth: '240px', maxWidth: '320px' }}>Reclamo</th>
              <th style={th('120px')}>Monto (S/)</th>
              <th style={th('220px')}>Estado</th>
              <th style={th('150px')}>Fecha inicio</th>
              <th style={th('120px')}>Fecha fin</th>
              <th style={th('200px')}>Registrado por</th>
              <th style={{ minWidth: '200px', maxWidth: '280px' }}>Observaciones</th>
              <th style={th('120px')}>Planilla</th>
              <th style={th('110px')}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {registros.length > 0 ? (
              registros.map((r) => (
                <tr key={r.id}>
                  <td>{r.nombre_usuario || '-'}</td>
                  <td style={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>{r.reclamo || '-'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatMonto(r.monto)}</td>
                  <td>{r.estado_descripcion || r.estado}</td>
                  <td>{formatFechaHora(r.fecha_inicio)}</td>
                  <td>{formatFecha(r.fecha_fin)}</td>
                  <td>{r.nombre_registra || '-'}</td>
                  <td style={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>{r.observaciones || '-'}</td>
                  <td>
                    <div className="acta-cell">{renderPlanillaCell(r)}</div>
                  </td>
                  <td>
                    <div className="induccion-acciones">
                      <ButtonUpdate title="Editar" onClick={() => setModalForm({ isOpen: true, row: r })} />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="sin-data">
                  No hay registros.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <ModalValidacionServicioForm
        isOpen={modalForm.isOpen}
        row={modalForm.row}
        usuarios={usuarios}
        estados={catalogo?.estados || []}
        onClose={() => setModalForm({ isOpen: false, row: null })}
        onSave={async (payload) => {
          if (modalForm.row) {
            await ValidacionServicioService.actualizar(payload);
          } else {
            await ValidacionServicioService.crear(payload);
          }
          await loadData();
        }}
      />

      <ModalValidacionServicioPlanilla
        isOpen={modalPlanilla.isOpen}
        row={modalPlanilla.row}
        onClose={() => setModalPlanilla({ isOpen: false, row: null })}
        onSave={async ({ id, archivo }) => {
          setIsLoading(true);
          try {
            await ValidacionServicioService.subirPlanilla(id, archivo, state.user?.usuario_id);
            await loadData();
          } finally {
            setIsLoading(false);
          }
        }}
      />
    </div>
  );
}

export default ValidacionServicio;
